import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import authTokenManager from '../utils/authToken.js';
import secureApiClient from '../utils/secureApiClient.js';
import rbac from '../utils/roleBasedAccess.js';

// Custom hook for secure data access
export const useSecureData = () => {
  const { currentUser } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize authentication and RBAC
  useEffect(() => {
    const initializeAuth = async () => {
      if (currentUser) {
        
        // Set user in RBAC system
        rbac.setUser(currentUser);
        setUserRole(rbac.getUserRole());
        setUserPermissions(rbac.getUserPermissions());
        
        // Check if we have a valid token
        try {
          const token = await authTokenManager.getValidAccessToken();
          const hasToken = !!token;
          setIsAuthenticated(hasToken);
        } catch (error) {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
        setUserPermissions([]);
      }
      
      setIsInitialized(true);
    };

    initializeAuth();
  }, [currentUser]);

  // Secure data fetching with authentication
  const fetchSecureData = useCallback(async (url, options = {}) => {
    try {
      // Wait for initialization
      if (!isInitialized) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Check authentication
      if (!isAuthenticated) {
        throw new Error('Authentication required');
      }

      // Check if token is valid
      const token = await authTokenManager.getValidAccessToken();
      if (!token) {
        throw new Error('Invalid or expired token');
      }

      // Make secure API call
      return await secureApiClient.makeSecureRequest(url, options);
    } catch (error) {
      throw error;
    }
  }, [isAuthenticated, isInitialized]);

  // Get filtered client data based on user role
  const getFilteredClientData = useCallback((clientData) => {
    if (!isAuthenticated) return null;

    // Determine data type based on content
    let dataType = 'client.basic';
    if (clientData.medical || clientData.billing) {
      dataType = 'client.personal';
    }
    if (clientData.medical) {
      dataType = 'client.medical';
    }
    if (clientData.billing && clientData.billing.cardNumber) {
      dataType = 'client.financial';
    }

    return rbac.filterSensitiveData(clientData, dataType);
  }, [isAuthenticated]);

  // Get filtered notes data based on user role
  const getFilteredNotesData = useCallback((notesData) => {
    if (!isAuthenticated) return null;

    const dataType = notesData.subjective || notesData.assessment ? 'notes.detailed' : 'notes.basic';
    return rbac.filterSensitiveData(notesData, dataType);
  }, [isAuthenticated]);

  // Check if user can perform an action
  const canPerform = useCallback((permission) => {
    return rbac.hasPermission(permission);
  }, []);

  // Check if user can access specific data type
  const canAccess = useCallback((dataType) => {
    return rbac.canAccessData(dataType);
  }, []);

  // Secure client operations
  const secureClientOperations = useMemo(() => ({
    // Get all clients with filtering
    getAllClients: async () => {
      try {
        const clients = await fetchSecureData(secureApiClient.baseURLs.cloudFunctions.getSheetData);
        return clients.map(client => getFilteredClientData(client)).filter(Boolean);
      } catch (error) {
        throw error;
      }
    },

    // Get all clients unfiltered (for Associates view — all therapists visible to all)
    getAllClientsUnfiltered: async () => {
      try {
        const url = secureApiClient.baseURLs.cloudFunctions.getSheetData + '?scope=associates';
        const clients = await fetchSecureData(url);
        return clients.map(client => getFilteredClientData(client)).filter(Boolean);
      } catch (error) {
        throw error;
      }
    },

    // Get single client with filtering
    getClient: async (clientId) => {
      try {
        const clients = await fetchSecureData(secureApiClient.baseURLs.cloudFunctions.getSheetData);
        const client = clients.find(c => c.id === clientId);
        return client ? getFilteredClientData(client) : null;
      } catch (error) {
        throw error;
      }
    },

    // Update client therapist (requires permission)
    updateClientTherapist: async (clientId, therapist) => {
      if (!canPerform('assign_therapists')) {
        throw new Error('Insufficient permissions to assign therapists');
      }

      try {
        return await secureApiClient.updateClientTherapist(clientId, therapist);
      } catch (error) {
        throw error;
      }
    },

    // Update client status (requires permission)
    updateClientStatus: async (clientId, status) => {
      if (!canPerform('edit_clients')) {
        throw new Error('Insufficient permissions to update client status');
      }

      try {
        return await secureApiClient.updateClientStatus(clientId, status);
      } catch (error) {
        throw error;
      }
    },

    // Update user role (admin only)
    updateUserRole: async (userId, role) => {
      if (!canPerform('manage_users')) {
        throw new Error('Insufficient permissions to update user roles');
      }

      try {
        return await secureApiClient.updateUserRole(userId, role);
      } catch (error) {
        throw error;
      }
    },

    // Create a profile without accepting a browser-supplied role.
    createUserProfile: async (firstName, lastName) => {
      try {
        return await secureApiClient.createUserProfile(firstName, lastName);
      } catch (error) {
        throw error;
      }
    }
  }), [fetchSecureData, getFilteredClientData, canPerform]);

  // Secure notes operations
  const secureNotesOperations = useMemo(() => ({
    // Get notes for a client with filtering
    getClientNotes: async (clientId) => {
      if (!canPerform('view_notes')) {
        throw new Error('Insufficient permissions to view notes');
      }
      return [];
    },

    // Create new note (requires permission)
    createNote: async (clientId, noteData) => {
      if (!canPerform('create_notes')) {
        throw new Error('Insufficient permissions to create notes');
      }

      try {
        // This would typically save to Firestore
        // For now, return success as placeholder
        return { success: true, noteId: 'temp_' + Date.now() };
      } catch (error) {
        throw error;
      }
    }
  }), [canPerform]);

  // Secure billing operations
  const secureBillingOperations = useMemo(() => ({
    // Get billing information with filtering
    getBillingInfo: async (clientId) => {
      if (!canPerform('view_billing')) {
        throw new Error('Insufficient permissions to view billing');
      }

      try {
        const clients = await fetchSecureData(secureApiClient.baseURLs.cloudFunctions.getSheetData);
        const client = clients.find(c => c.id === clientId);
        
        if (!client) return null;

        const billingData = client.billing || {};
        return rbac.filterSensitiveData(billingData, 'billing.detailed');
      } catch (error) {
        throw error;
      }
    }
  }), [canPerform, fetchSecureData]);

  return {
    // Authentication state
    isAuthenticated,
    userRole,
    userPermissions,
    isInitialized,
    
    // Permission checks
    canPerform,
    canAccess,
    
    // Data filtering
    getFilteredClientData,
    getFilteredNotesData,
    
    // Secure operations
    secureClientOperations,
    secureNotesOperations,
    secureBillingOperations,
    
    // Direct API access
    fetchSecureData,
    
    // Utility functions
    refreshToken: authTokenManager.refreshAccessToken,
    clearTokens: authTokenManager.clearTokens
  };
};

// Hook for secure data with automatic refresh
export const useSecureDataWithRefresh = (dataFetcher, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated, fetchSecureData } = useSecureData();

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) {
      setData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await dataFetcher(fetchSecureData);
      setData(result);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dataFetcher, fetchSecureData, ...dependencies]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refresh,
    isAuthenticated
  };
};

// Hook for real-time secure data
export const useSecureRealtimeData = (dataType, clientId = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  const { isAuthenticated, canAccess } = useSecureData();

  useEffect(() => {
    if (!isAuthenticated || !canAccess(dataType)) {
      setData(null);
      setLoading(false);
      return;
    }

    // Set up real-time listeners here
    // For now, this is a placeholder for Firestore real-time listeners
    setLoading(false);
  }, [isAuthenticated, canAccess, dataType, clientId]);

  return {
    data,
    loading,
    error,
    isAuthenticated
  };
};
